import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import { salesManagement } from "../../../config";
import PaymentVoucherForm from "../PaymentVoucher/form";
import { useEffect, useState, useCallback, useRef } from "react";
import PurchaseOrderForm from "./form";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { AllVendors } from "../../../data/Axios/queries";
import { tokens } from "../../../theme";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import usePermissions from "../../../data/RBAC/PermissionsHook";

const base_url = salesManagement.uri;

const SEARCH_PURCHASE_ORDERS = `
  query SearchPurchaseOrders($parameters: SearchPurchaseOrdersInput!) {
    searchPurchaseOrders(parameters: $parameters) {
      items {
        id
        purchaseOrder
        vendorName
        vendorComments
        vendorId
        orderDate
        totalAmount
        branchName
        paymentStatus
        deliveryStatus
        productCode
        createdDate
        createdBy
        updatedDate
        expiryDate
        updatedBy
        companyId
        companyName
        isActive
        outstandingBalance
        modifiedBy
        addedBy
        branchId
        reversed
      }
      totalCount
    }
  }
`;

const actions = {
  entity: "PurchaseOrder",
  isSubRight: true,
  edit: {
    key: "EDIT",
    button_name: "Edit",
    Show_Button: false,
    permission: "EditPermission",
  },
  add: {
    key: "ADD",
    button_name: "Add",
    Show_Button: true,
    permission: "CreatePermission",
  },
  markIsDelivered: {
    key: "MARK_TASK_DONE",
    button_name: "Mark Delivered",
    Show_Button: true,
    swalName: "delivered",
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    dataObject: {
      inputObjectName: "confirmPurchaseOrderDelivery",
      inputFields: ["applicationId", "userId", "comments"],
    },
  },
  createVoucher: {
    key: "createVoucher",
    modal: true,
    button_name: "Create Payment Voucher",
    Show_Button: true,
    permission: "CreatePermission",
    roleGroupId: [1, 2, 3, 4],
    dataObject: {
      inputObjectName: "createpaymentvoucher",
      inputFields: ["applicationId", "userId", "comments"],
    },
  },
  ViewPurchaseOrderItems: {
    key: "MARK_TASK_DONE",
    button_name: "View Order Items",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "EditPermission",
    title: "Purchase Order Details",
    redirect: "/payment-voucher",
  },
  ReversePurchaseOrder: {
    key: "MARK_TASK_DONE",
    button_name: "Reverse Purchase Order",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  ViewPOPaymentMethods: {
    key: "MARK_TASK_DONE",
    button_name: "View Payment Methods",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },
  showStatus: true,
  showDelivery: true,
  showPayments: true,
  restrict: {
    restrict: true,
    field: "productCode",
    value: "",
  }
};

function PurchaseOrderTable() {
  const [orders, setOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [vendorFilter, setVendorFilter] = useState("all");
  const { hasPermission } = usePermissions();

  // Pagination - 0-based for MUI DataGrid
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  // For vendor/branch dropdowns
  const [branches, setBranches] = useState([]);
  const [vendors, setVendors] = useState([]);

  // Ref to prevent multiple simultaneous requests
  const fetchInProgress = useRef(false);

  const [stats, setStats] = useState({
    totalOrders: 0,
    totalAmount: 0,
    paidOrders: 0,
    partiallyPaidOrders: 0,
    unpaidOrders: 0,
    pendingApprovalOrders: 0,
    deliveredOrders: 0,
    pendingDeliveries: 0,
    outstandingBalance: 0,
    averageOrderValue: 0,
    averageOutstanding: 0,
  });

  // MUI theme-aware colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
    iconBadgeGreen: isDark ? alpha("#4CAF50", 0.15) : "#E8F5E9",
    iconBadgeOrange: isDark ? alpha("#FF9800", 0.15) : "#FFF3E0",
    iconBadgeRed: isDark ? alpha("#F44336", 0.15) : "#FFEBEE",
  };

  const ignore = [
    "deliveryStatus",
    "paymentStatus",
    "expiryDate",
    "createdBy",
    "updatedBy",
    "productCode",
    "vendorId",
    "companyId",
    "createdDate",
    "createdBy",
    "updatedDate",
    "companyName",
    "addedBy",
    "modifiedBy",
  ];

  const ignoreshowMore = ["createdBy", "updatedBy", "vendorId", "companyId"];

  // ── Load all vendors once on mount for the filter dropdown ──
  useEffect(() => {
    const loadAllVendors = async () => {
      try {
        const response = await graphqlQuery(AllVendors, base_url);
        const vendorList = Array.isArray(response)
          ? response
          : response?.allVendors || [];
        const names = vendorList
          .map((v) => v.vendorName)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setVendors(names);
      } catch (err) {
        console.error("Failed to load vendor list:", err);
      }
    };
    loadAllVendors();
  }, []);

  // ── Load all branch names once on mount (derived from allPurchaseOrdersView) ──
  // We fetch a wide page to collect all unique branch names for the filter.
  useEffect(() => {
    const loadAllBranches = async () => {
      try {
        const parameters = {
          pageNumber: 1,
          pageSize: 500, // large enough to capture all distinct branches
        };
        const response = await graphqlQuery(
          SEARCH_PURCHASE_ORDERS,
          base_url,
          { parameters }
        );
        const items =
          response?.searchPurchaseOrders?.items ||
          response?.items ||
          [];
        const uniqueBranches = [
          ...new Set(items.map((o) => o.branchName).filter(Boolean)),
        ].sort();
        setBranches(uniqueBranches);
      } catch (err) {
        console.error("Failed to load branch list:", err);
      }
    };
    loadAllBranches();
  }, []);

  // const handleRefetch = useCallback(() => {
  //   // Force a reload with current filters and pagination
  //   loadPurchaseOrders();
  // }, [loadPurchaseOrders]); // Add loadPurchaseOrders to dependencies
  const loadPurchaseOrders = useCallback(async () => {
    if (fetchInProgress.current) {
      console.log("Fetch already in progress, skipping...");
      return;
    }

    setLoading(true);
    setError("");
    fetchInProgress.current = true;


    try {
      const parameters = {
        searchText: searchQuery.trim() || null,
        vendorName: vendorFilter === "all" ? null : vendorFilter,
        branchName: branchFilter === "all" ? null : branchFilter,
        paymentStatus: paymentStatusFilter === "all" ? null : Number(paymentStatusFilter),
        deliveryStatus: deliveryStatusFilter === "all" ? null : Number(deliveryStatusFilter),
        isActive: activeFilter === "all" ? null : activeFilter === "1",
        dateFrom: dateFrom ? `${dateFrom}T00:00:00.000` : null,
        dateTo: dateTo
      ? `${dateTo}T23:59:59.999`
      : dateFrom
        ? `${new Date().toLocaleDateString("en-CA")}T23:59:59.999`
        : null,
        pageNumber: page + 1,
        pageSize: pageSize,
      };

      console.log("Fetching with parameters:", parameters);

      const response = await graphqlQuery(
        SEARCH_PURCHASE_ORDERS,
        base_url,
        { parameters }
      );

      const searchResults = response?.searchPurchaseOrders || response;
      const items = searchResults?.items || [];
      const total = searchResults?.totalCount || items.length;

      const activeOrders = items.filter((o) => !o.reversed);

      // Keep raw backend timestamps so shared table/modal formatters can render
      // consistently with Africa/Nairobi handling.
      const formattedData = activeOrders.map((order) => ({ ...order }));

      setOrders(formattedData);
      setRowCount(total);
      setTotalCount(total);

      calculateStats(formattedData);

    } catch (err) {
      console.error("Error loading purchase orders:", err);
      setError("Failed to load purchase orders. Please try again.");
    } finally {
      setLoading(false);
      fetchInProgress.current = false;
    }
  }, [
    searchQuery,
    vendorFilter,
    branchFilter,
    paymentStatusFilter,
    deliveryStatusFilter,
    activeFilter,
    dateFrom,
    dateTo,
    page,
    pageSize,
  ]);

  // Update useEffect dependencies
  useEffect(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  const handleRefresh = useCallback(() => {
    loadPurchaseOrders();
  }, [loadPurchaseOrders]);

  const calculateStats = (data) => {
    const totalOrders = data.length;
    const totalAmountSum = data.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const outstandingSum = data.reduce((sum, o) => sum + (o.outstandingBalance || 0), 0);

    const paid = data.filter((o) => o.paymentStatus === 2).length;
    const partial = data.filter((o) => o.paymentStatus === 1).length;
    const unpaid = data.filter((o) => o.paymentStatus === 0).length;
    const pendingApproval = data.filter((o) => o.paymentStatus === 3).length;

    const delivered = data.filter((o) => o.deliveryStatus === 1).length;
    const pendingDelivery = data.filter((o) => o.deliveryStatus === 0).length;

    const avgOrder = totalOrders > 0 ? totalAmountSum / totalOrders : 0;
    const ordersWithBalance = data.filter((o) => (o.outstandingBalance || 0) > 0);
    const avgOutstanding =
      ordersWithBalance.length > 0 ? outstandingSum / ordersWithBalance.length : 0;

    setStats({
      totalOrders,
      totalAmount: totalAmountSum,
      paidOrders: paid,
      partiallyPaidOrders: partial,
      unpaidOrders: unpaid,
      pendingApprovalOrders: pendingApproval,
      deliveredOrders: delivered,
      pendingDeliveries: pendingDelivery,
      outstandingBalance: outstandingSum,
      averageOrderValue: avgOrder,
      averageOutstanding: avgOutstanding,
    });

    const formattedTotal = totalAmountSum.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setTotalAmount(formattedTotal);
  };

  // const handleRefresh = () => {
  //   loadPurchaseOrders();
  // };

  const handleClearFilters = () => {
    setSearchQuery("");
    setPaymentStatusFilter("all");
    setDeliveryStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setActiveFilter("all");
    setBranchFilter("all");
    setVendorFilter("all");
    setPage(0);
  };

  const hasActiveFilters =
    searchQuery ||
    paymentStatusFilter !== "all" ||
    deliveryStatusFilter !== "all" ||
    dateFrom ||
    dateTo ||
    activeFilter !== "all" ||
    branchFilter !== "all" ||
    vendorFilter !== "all";

  const handlePageChange = (newPage) => {
    console.log("Page changed to:", newPage);
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    console.log("Page size changed to:", newPageSize);
    setPageSize(newPageSize);
    setPage(0);
  };


  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Purchase Order Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Track and manage all purchase orders and vendor transactions
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
{hasPermission("PurchaseOrder", "ViewStatsPermission") && (
  <>
      {/* Stats Cards - keeping your existing code */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: isDark
                ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`
                : `linear-gradient(135deg, ${theme.primary} 0%, ${theme.secondary} 100%)`,
              color: "white",
              border: isDark ? `1px solid ${alpha(colors.blueAccent[400], 0.4)}` : "none",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <AttachMoneyIcon sx={{ fontSize: 28, mr: 1 }} />
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Total Amount
                </Typography>
              </Box>
              <Typography variant="h4" fontWeight="700">
                KSh {totalAmount}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                {stats.totalOrders} orders • Avg: KSh{" "}
                {stats.averageOrderValue.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Other stats cards - keeping your existing code */}
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Payment Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                    <Box>
                      <Typography variant="h6" fontWeight="700" color={theme.success}>
                        {stats.paidOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Paid
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h6" fontWeight="700" color="orange">
                        {stats.partiallyPaidOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Partial
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h6" fontWeight="700" color={theme.error}>
                        {stats.unpaidOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Unpaid
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h6" fontWeight="700" color="blue">
                        {stats.pendingApprovalOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeGreen, p: 1.5, borderRadius: 2 }}>
                  <AccountBalanceIcon sx={{ color: theme.success, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Delivery Status
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.success}>
                        {stats.deliveredOrders}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Delivered
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box>
                      <Typography variant="h5" fontWeight="700" color={theme.warning}>
                        {stats.pendingDeliveries}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeOrange, p: 1.5, borderRadius: 2 }}>
                  <LocalShippingIcon sx={{ color: theme.warning, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={0} sx={{ bgcolor: theme.cardBg, height: "100%" }}>
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="600"
                  >
                    Outstanding Balance
                  </Typography>
                  <Typography variant="h4" fontWeight="700" color={theme.error}>
                    KSh{" "}
                    {stats.outstandingBalance.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Avg: KSh{" "}
                    {stats.averageOutstanding.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: theme.iconBadgeRed, p: 1.5, borderRadius: 2 }}>
                  <PendingActionsIcon sx={{ color: theme.error, fontSize: 28 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </>
)}

      {/* Filters Section - keeping your existing code */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: theme.cardBg,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${[
                searchQuery && "Search",
                paymentStatusFilter !== "all" && "Payment",
                deliveryStatusFilter !== "all" && "Delivery",
                dateFrom && "From Date",
                dateTo && "To Date",
                activeFilter !== "all" && "Status",
                branchFilter !== "all" && "Branch",
                vendorFilter !== "all" && "Vendor",
              ].filter(Boolean).length} Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by PO#, vendor, company..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(0); // Reset to first page on search
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Vendor</InputLabel>
              <Select
                value={vendorFilter}
                label="Vendor"
                onChange={(e) => {
                  setVendorFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Vendors</MenuItem>
                {vendors.map((vendor) => (
                  <MenuItem key={vendor} value={vendor}>
                    {vendor}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Branch</InputLabel>
              <Select
                value={branchFilter}
                label="Branch"
                onChange={(e) => {
                  setBranchFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <StorefrontIcon sx={{ fontSize: 18, color: theme.primary }} />
                      {branch}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment Status"
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="2">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Paid
                  </Box>
                </MenuItem>
                <MenuItem value="1">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 18, color: "orange" }} />
                    Partially Paid
                  </Box>
                </MenuItem>
                <MenuItem value="0">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CancelIcon sx={{ fontSize: 18, color: theme.error }} />
                    Unpaid
                  </Box>
                </MenuItem>
                <MenuItem value="3">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 18, color: "blue" }} />
                    Pending Approval
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Delivery Status</InputLabel>
              <Select
                value={deliveryStatusFilter}
                label="Delivery Status"
                onChange={(e) => {
                  setDeliveryStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Delivered
                  </Box>
                </MenuItem>
                <MenuItem value="0">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocalShippingIcon sx={{ fontSize: 18, color: theme.warning }} />
                    Pending
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(0);
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={(e) => {
                  setActiveFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={1}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active filter chips - keeping your existing code */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {vendorFilter !== "all" && (
              <Chip
                label={`Vendor: ${vendorFilter}`}
                size="small"
                onDelete={() => setVendorFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {branchFilter !== "all" && (
              <Chip
                label={`Branch: ${branchFilter}`}
                size="small"
                onDelete={() => setBranchFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentStatusFilter !== "all" && (
              <Chip
                label={`Payment: ${paymentStatusFilter === "2"
                  ? "Paid"
                  : paymentStatusFilter === "1"
                    ? "Partially Paid"
                    : paymentStatusFilter === "0"
                      ? "Unpaid"
                      : "Pending Approval"
                  }`}
                size="small"
                onDelete={() => setPaymentStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {deliveryStatusFilter !== "all" && (
              <Chip
                label={`Delivery: ${deliveryStatusFilter === "1" ? "Delivered" : "Pending"}`}
                size="small"
                onDelete={() => setDeliveryStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFrom && (
              <Chip
                label={`From: ${dateFrom}`}
                size="small"
                onDelete={() => setDateFrom("")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateTo && (
              <Chip
                label={`To: ${dateTo}`}
                size="small"
                onDelete={() => setDateTo("")}
                color="primary"
                variant="outlined"
              />
            )}
            {activeFilter !== "all" && (
              <Chip
                label={`Status: ${activeFilter === "1" ? "Active" : "Inactive"}`}
                size="small"
                onDelete={() => setActiveFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* ── FIXED: DynamicTable with proper server-side pagination ── */}
      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden", bgcolor: theme.cardBg }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Purchase Orders"
            subtitle={`Showing ${orders.length} of ${rowCount} orders${hasActiveFilters ? " (filtered)" : ""}`}

            // ── Server-side data ──
            rows={orders}                    // Current page data
            rowCount={rowCount}              // Total count from backend
            page={page}                      // 0-based for MUI
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            paginationMode="server"          // Enable server-side mode
            pagination={true}

            // Other props
            ignoreFields={ignore}
            ignoreShowMore={ignoreshowMore}
            FormComponent={PurchaseOrderForm}
            FormComponent1={PaymentVoucherForm}
            query={SEARCH_PURCHASE_ORDERS}
            base_url={base_url}
            actions={actions}
            onServerAction={handleRefresh}  // Pass the refetch function here
            getRowId={(row) => row.id}
            checkboxSelection={false}
          />
        )}
      </Paper>
    </Box>
  );
}

export default PurchaseOrderTable;
